import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Award, Search, Filter, Calendar, Car, Bike, RefreshCw, BarChart3, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { getEventTournamentBrackets, getTournamentBracket } from '../../lib/ladderPointsUtils';
import {
  assignPlacesByPoints,
  canonicalCategoryName,
  placesByRaceDay,
  raceDayPlaceKey
} from '../../lib/pointsStandings';

// One row of the `event_driver_points` view: what a driver scored in one category on one race day.
// The view works out the total from the stage reached plus the round 1, entry and reaction awards;
// drivers and spectators only ever see that total, so the breakdown is not fetched here.
interface PointsRow {
  event_id: string;
  event_name: string;
  event_date: string;
  category_id: string;
  category_name: string;
  category_vehicle_type: string;
  driver_key: string;
  driver_id: string | null;
  temporary_user_id: string | null;
  driver_name: string;
  race_number: string;
  total_points: number;
}

const POINTS_COLUMNS =
  'event_id, event_name, event_date, category_id, category_name, category_vehicle_type, driver_key, driver_id, temporary_user_id, driver_name, race_number, total_points';

// A race day row with the place it earned in its category
interface RaceDayResult extends PointsRow {
  position: number;
}

interface DriverLeaderboardEntry {
  driver_id: string;
  driver_name: string;
  race_number: string;
  category_id: string;
  category_name: string;
  total_points: number;
  total_events: number;
  position: number;
}

// A driver's running total for one series in one season
interface SeriesPointsEntry {
  driver_id: string | null;
  temporary_user_id?: string | null;
  driver_name: string;
  race_number: string;
  series_name: string;
  season: number;
  total_points: number;
  events_participated: number;
  position: number;
  category_name: string;
  vehicle_type: string;
  events: RaceDayResult[];
}

interface EventOption {
  id: string;
  name: string;
  event_date: string;
}

// Helper to get the series name of an event (e.g. "Street Ironman Cup - Jan 15" -> "Street Ironman Cup")
function seriesNameOf(eventName?: string | null) {
  return eventName?.split(' - ')[0] || eventName || 'Unknown Series';
}

// Race numbers are entered inconsistently (e.g. "M410" and "410" are the same bike), so compare loosely
function normalizedRaceNumber(raceNumber?: string | null) {
  return (raceNumber || '').replace(/^M/i, '').trim();
}

// A series runs again every year, so standings are kept per season (the calendar year of the race)
function seasonOf(eventDate?: string | null): number {
  const year = Number.parseInt((eventDate || '').slice(0, 4));
  return Number.isNaN(year) ? 0 : year;
}

// Helper to format date for display
function formatDateForDisplay(dateString: string): string {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day));
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function TournamentResults() {
  const { user } = useAuth();
  const [results, setResults] = useState<RaceDayResult[]>([]);
  const [leaderboardByCategory, setLeaderboardByCategory] = useState<{ [category: string]: DriverLeaderboardEntry[] }>({});
  const [categoryStats, setCategoryStats] = useState<{ [category: string]: { driverCount: number; fee: number; reEntryCount: number } }>({});
  const [categoryTotalAmount, setCategoryTotalAmount] = useState<{ [categoryId: string]: number }>({});
  const [categoryPrizePercentages, setCategoryPrizePercentages] = useState<{ [category: string]: { first: number; second: number } }>({});
  const [categoryPrizeInfo, setCategoryPrizeInfo] = useState<{ [category: string]: { usePercentage: boolean; firstPct: number; secondPct: number; firstFixed: number; secondFixed: number } }>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'my-results' | 'leaderboard' | 'series-points'>(
    user ? 'my-results' : 'leaderboard'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState<'all' | 'car' | 'bike'>('all');
  const [events, setEvents] = useState<EventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  
  // New state for series points
  const [seriesPoints, setSeriesPoints] = useState<SeriesPointsEntry[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  
  // New state for driver details overlay
  const [selectedDriver, setSelectedDriver] = useState<SeriesPointsEntry | null>(null);
  const [showDriverOverlay, setShowDriverOverlay] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchLeaderboard(selectedEventId);
    }
  }, [selectedEventId]);

  // New useEffect to fetch series points when tab changes
  useEffect(() => {
    if (activeTab === 'series-points') {
      fetchSeriesPoints();
    }
  }, [activeTab]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('events')
        .select('id, name, event_date')
        .eq('is_active', true)
        .order('event_date', { ascending: false });
      if (error) throw error;
      setEvents(data || []);
      if (data && data.length > 0) {
        setSelectedEventId(data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('event_driver_points')
        .select(POINTS_COLUMNS)
        .eq('driver_id', user.id)
        .order('event_date', { ascending: false });

      if (error) throw error;

      const myRows = (data || []) as PointsRow[];

      // A place only means something next to the rest of the category, so load those race days in full
      const eventIds = Array.from(new Set(myRows.map(row => row.event_id)));
      let places = new Map<string, number>();

      if (eventIds.length > 0) {
        const { data: raceDayRows, error: raceDayError } = await supabase
          .from('event_driver_points')
          .select(POINTS_COLUMNS)
          .in('event_id', eventIds);

        if (raceDayError) throw raceDayError;
        places = placesByRaceDay((raceDayRows || []) as PointsRow[]);
      }

      setResults(myRows.map(row => ({ ...row, position: places.get(raceDayPlaceKey(row)) || 0 })));
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load your tournament results');
    } finally {
      setLoading(false);
    }
  };

  // Series points: a driver's race-day totals summed across the dates of one series, per season
  const fetchSeriesPoints = async () => {
    try {
      setSeriesLoading(true);

      const { data, error } = await supabase
        .from('event_driver_points')
        .select(POINTS_COLUMNS)
        .order('event_date', { ascending: true });

      if (error) throw error;

      const places = placesByRaceDay((data || []) as PointsRow[]);

      // Bucket per category, series and season. The season keeps a yearly re-run of the same
      // series (e.g. Ironman) from inheriting last year's points.
      const buckets = new Map<
        string,
        { categoryName: string; seriesName: string; season: number; rows: PointsRow[] }
      >();

      for (const row of (data || []) as PointsRow[]) {
        const categoryName = canonicalCategoryName(row.category_name || 'Unknown Category');
        const seriesName = seriesNameOf(row.event_name);
        const season = seasonOf(row.event_date);
        const key = `${categoryName}|${seriesName}|${season}`;

        if (!buckets.has(key)) {
          buckets.set(key, { categoryName, seriesName, season, rows: [] });
        }
        buckets.get(key)!.rows.push(row);
      }

      const entries: SeriesPointsEntry[] = [];

      for (const { categoryName, seriesName, season, rows } of buckets.values()) {
        // The same person can appear under a different registration or temporary user on each race
        // day, so merge rows that agree on any identifier: race number, driver id or driver name
        const groups: PointsRow[][] = [];

        for (const row of rows) {
          const raceNumber = normalizedRaceNumber(row.race_number);
          const name = (row.driver_name || '').toLowerCase().trim();

          const group = groups.find(candidate =>
            candidate.some(other => {
              const otherRaceNumber = normalizedRaceNumber(other.race_number);
              const otherName = (other.driver_name || '').toLowerCase().trim();

              return (
                (!!raceNumber && !!otherRaceNumber && raceNumber === otherRaceNumber) ||
                (!!row.driver_id && !!other.driver_id && row.driver_id === other.driver_id) ||
                (!!name && !!otherName && name === otherName)
              );
            })
          );

          if (group) {
            group.push(row);
          } else {
            groups.push([row]);
          }
        }

        for (const group of groups) {
          const preferred = group.find(row => row.driver_id) || group[0];

          entries.push({
            driver_id: preferred.driver_id || null,
            temporary_user_id: preferred.driver_id ? null : preferred.temporary_user_id,
            driver_name: preferred.driver_name || 'Unknown Driver',
            race_number: normalizedRaceNumber(preferred.race_number) || 'no_race_number',
            series_name: seriesName,
            season,
            total_points: group.reduce((total, row) => total + (row.total_points || 0), 0),
            events_participated: new Set(group.map(row => row.event_date)).size,
            position: 0, // Series place, assigned per season when the standings are built
            category_name: categoryName,
            vehicle_type: preferred.category_vehicle_type || 'car',
            events: [...group]
              .sort((a, b) => a.event_date.localeCompare(b.event_date))
              .map(row => ({ ...row, position: places.get(raceDayPlaceKey(row)) || 0 }))
          });
        }
      }

      setSeriesPoints(entries);
      // Keep the chosen season when it still has results, otherwise show the most recent one
      setSelectedSeason(previousSeason => {
        if (previousSeason !== null && entries.some(entry => entry.season === previousSeason)) {
          return previousSeason;
        }
        const latestSeason = entries.reduce((latest, entry) => Math.max(latest, entry.season), 0);
        return latestSeason || null;
      });
    } catch (error) {
      console.error('Error fetching series points:', error);
      toast.error('Failed to load series points');
    } finally {
      setSeriesLoading(false);
    }
  };

  // Every race day making up a driver's series total is already loaded, so the overlay needs no query
  const openDriverDetails = (driver: SeriesPointsEntry) => {
    setSelectedDriver(driver);
    setShowDriverOverlay(true);
  };

  const fetchLeaderboard = async (eventId: string) => {
    try {
      setLoading(true);
      // Fetch all brackets for the event
      const brackets = await getEventTournamentBrackets(eventId);
      // Map categoryId to bracketId
      const categoryToBracket: { [categoryId: string]: string } = {};
      const categoryToBracketName: { [categoryId: string]: string } = {};
      const categoryToFee: { [categoryId: string]: number } = {};
      const categoryToPrizePercentages: { [category: string]: { first: number; second: number } } = {};
      for (const bracket of brackets) {
        categoryToBracket[bracket.category_id] = bracket.id;
        const cat = Array.isArray((bracket as any)['event_categories']) ? (bracket as any)['event_categories'][0] : (bracket as any)['event_categories'];
        const canonicalName = canonicalCategoryName(cat?.name || 'Unknown Category');
        categoryToBracketName[bracket.category_id] = cat?.name || 'Unknown Category';
        categoryToFee[bracket.category_id] = cat?.registration_fee || 0;
        categoryToPrizePercentages[canonicalName] = {
          first: cat?.first_place_percentage || 0,
          second: cat?.second_place_percentage || 0
        };
      }
      setCategoryPrizePercentages(categoryToPrizePercentages);
      // Fetch all matches for all brackets
      const allMatches: { [bracketId: string]: any[] } = {};
      for (const bracket of brackets) {
        const { matches } = await getTournamentBracket(bracket.id);
        allMatches[bracket.id] = matches;
      }
      // One row per driver per category for this event, already including the bonus awards
      const { data: pointsRows, error: pointsError } = await supabase
        .from('event_driver_points')
        .select(POINTS_COLUMNS)
        .eq('event_id', eventId);
      if (pointsError) throw pointsError;

      const driverCategoryMap = new Map<string, DriverLeaderboardEntry>();
      for (const row of (pointsRows || []) as PointsRow[]) {
        const canonicalName = canonicalCategoryName(row.category_name || 'Unknown Category');
        const key = `${row.driver_key}-${canonicalName}`;

        const existing = driverCategoryMap.get(key);
        if (!existing) {
          driverCategoryMap.set(key, {
            driver_id: row.driver_key,
            driver_name: row.driver_name || 'Unknown Driver',
            race_number: row.race_number || '',
            category_id: row.category_id,
            category_name: canonicalName,
            total_points: row.total_points || 0,
            total_events: 1,
            position: 0 // Assigned below, once every driver's total for the category is known
          });
          continue;
        }

        // A re-entry category maps onto the same canonical name, so fold the two together
        existing.total_points += row.total_points || 0;
        existing.total_events += 1;
      }

      const mappedDrivers = Array.from(driverCategoryMap.values());
      // Group by canonical category
      const byCategory: { [category: string]: DriverLeaderboardEntry[] } = {};
      const stats: { [category: string]: { driverCount: number; fee: number; reEntryCount: number } } = {};
      for (const driver of mappedDrivers) {
        if (!byCategory[driver.category_name]) byCategory[driver.category_name] = [];
        byCategory[driver.category_name].push(driver);
      }
      for (const drivers of Object.values(byCategory)) {
        assignPlacesByPoints(drivers);
      }
      // Calculate stats for each canonical category
      for (const [category, drivers] of Object.entries(byCategory)) {
        // Find the bracketId for this category (pick the first matching bracket)
        const categoryId = drivers[0]?.category_id;
        const bracketId = categoryToBracket[categoryId];
        // Total driver count
        const driverCount = drivers.length;
        // Category fee (keep as before, but not used for display)
        const fee = categoryToFee[categoryId] || 0;
        // Calculate re-entry count from matches
        const matches = allMatches[bracketId] || [];
        // Map: registration_id -> array of rounds they participated in
        const participation: { [registrationId: string]: number[] } = {};
        // Map: registration_id -> array of rounds they lost in
        const eliminations: { [registrationId: string]: number[] } = {};
        for (const match of matches) {
          if (match.driver1_registration_id) {
            if (!participation[match.driver1_registration_id]) participation[match.driver1_registration_id] = [];
            participation[match.driver1_registration_id].push(match.round_number);
          }
          if (match.driver2_registration_id) {
            if (!participation[match.driver2_registration_id]) participation[match.driver2_registration_id] = [];
            participation[match.driver2_registration_id].push(match.round_number);
          }
          if (match.loser_registration_id) {
            if (!eliminations[match.loser_registration_id]) eliminations[match.loser_registration_id] = [];
            eliminations[match.loser_registration_id].push(match.round_number);
          }
        }
        // For each registration, if eliminated in round N and appears in round N+1 or later, count as re-entry
        let reEntryCount = 0;
        for (const regId in eliminations) {
          const lostRounds = eliminations[regId];
          const playedRounds = participation[regId] || [];
          for (const lostRound of lostRounds) {
            if (playedRounds.some(r => r > lostRound)) {
              reEntryCount++;
            }
          }
        }
        stats[category] = { driverCount, fee, reEntryCount };
      }
      setLeaderboardByCategory(byCategory);
      setCategoryStats(stats);

      // Fetch all event registrations for the selected event and merge by canonical category name
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select('amount_paid, category_id, event_id, category:event_categories(name)')
        .eq('event_id', eventId);
      const totalAmount: { [category: string]: number } = {};
      if (registrations) {
        for (const reg of registrations) {
          let catNameRaw = '';
          if (Array.isArray(reg.category)) {
            const firstCat = (reg.category as any[])[0];
            catNameRaw = (firstCat && typeof firstCat === 'object' && 'name' in firstCat) ? (firstCat as any).name : '';
          } else {
            catNameRaw = reg.category && typeof reg.category === 'object' && 'name' in reg.category ? (reg.category as any).name : '';
          }
          const catName = canonicalCategoryName(catNameRaw);
          if (!totalAmount[catName]) totalAmount[catName] = 0;
          totalAmount[catName] += reg.amount_paid || 0;
        }
      }
      setCategoryTotalAmount(totalAmount);

      // Fetch event_categories for the event
      const { data: eventCategories, error: catError } = await supabase
        .from('event_categories')
        .select('name, use_percentage_prizes, first_place_percentage, second_place_percentage, first_place_prize, second_place_prize, event_id')
        .eq('event_id', eventId);
      if (catError) throw catError;
      // Map canonical category name to prize info
      const catPrizeInfo: { [category: string]: { usePercentage: boolean; firstPct: number; secondPct: number; firstFixed: number; secondFixed: number } } = {};
      if (eventCategories) {
        for (const cat of eventCategories) {
          const canonicalName = canonicalCategoryName(cat.name || 'Unknown Category');
          catPrizeInfo[canonicalName] = {
            usePercentage: !!cat.use_percentage_prizes,
            firstPct: cat.first_place_percentage || 0,
            secondPct: cat.second_place_percentage || 0,
            firstFixed: cat.first_place_prize || 0,
            secondFixed: cat.second_place_prize || 0
          };
        }
      }
      setCategoryPrizeInfo(catPrizeInfo);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getPositionDisplay = (position: number): string => {
    if (position === 1) return '1st';
    if (position === 2) return '2nd';
    if (position === 3) return '3rd';
    return `${position}th`;
  };

  const getPositionColor = (position: number): string => {
    if (position === 1) return 'text-yellow-500';
    if (position === 2) return 'text-gray-400';
    if (position === 3) return 'text-amber-600';
    return 'text-white';
  };

  // Seasons that actually have series results, most recent first
  const availableSeasons = useMemo(
    () => Array.from(new Set(seriesPoints.map(entry => entry.season))).sort((a, b) => b - a),
    [seriesPoints]
  );

  // Standings are built per season so a yearly re-run of a series starts from zero
  const seriesPointsByCategory = useMemo(() => {
    const grouped: { [category: string]: SeriesPointsEntry[] } = {};
    if (selectedSeason === null) return grouped;

    for (const entry of seriesPoints) {
      if (entry.season !== selectedSeason) continue;
      if (!grouped[entry.category_name]) grouped[entry.category_name] = [];
      grouped[entry.category_name].push(entry);
    }

    for (const category in grouped) {
      assignPlacesByPoints(grouped[category]);
    }

    return grouped;
  }, [seriesPoints, selectedSeason]);

  const filteredResults = results.filter(result => {
    const matchesSearch = 
      result.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.category_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesVehicleFilter = 
      vehicleFilter === 'all' || 
      result.category_vehicle_type === vehicleFilter;
    
    return matchesSearch && matchesVehicleFilter;
  });

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Tournament Results</h1>
            <p className="text-gray-400">View points by event, category, and series</p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="event-select" className="text-gray-300 text-sm mr-2">Event:</label>
            <select
              id="event-select"
              className="bg-zinc-900 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:ring-2 focus:ring-primary-500"
              value={selectedEventId}
              onChange={e => setSelectedEventId(e.target.value)}
            >
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.name} ({formatDateForDisplay(event.event_date)})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-zinc-700">
            <nav className="-mb-px flex space-x-8">
              {user && (
                <button
                  onClick={() => setActiveTab('my-results')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'my-results'
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Trophy className="inline-block w-4 h-4 mr-2" />
                  My Results
                </button>
              )}
              <button
                onClick={() => setActiveTab('leaderboard')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'leaderboard'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <Award className="inline-block w-4 h-4 mr-2" />
                Event Leaderboard
              </button>
              <button
                onClick={() => setActiveTab('series-points')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'series-points'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="inline-block w-4 h-4 mr-2" />
                Series Points
              </button>
            </nav>
          </div>
        </div>

        {/* My Results Tab */}
        {activeTab === 'my-results' && user && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">My Tournament Results</h2>
              <button
                onClick={fetchResults}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
            
            {results.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 rounded-lg">
                <Trophy className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">No results found</h3>
                <p className="text-gray-400">You haven't participated in any tournaments yet.</p>
              </div>
            ) : (
              <div className="bg-zinc-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-700">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Event</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Position</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Points</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-700">
                      {results.map((result) => (
                        <tr key={`${result.event_id}-${result.category_id}`} className="hover:bg-zinc-750">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{result.event_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300">{result.category_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300">{formatDateForDisplay(result.event_date)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${getPositionColor(result.position)}`}>
                              {getPositionDisplay(result.position)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-primary-500">{result.total_points}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Event Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="text-gray-400 mt-4">Loading leaderboard...</p>
              </div>
            ) : Object.keys(leaderboardByCategory).length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 rounded-lg">
                <Award className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">
                  No tournament results for this event
                </h3>
                <p className="text-gray-400">
                  Select another event to view results.
                </p>
              </div>
            ) : (
              Object.entries(leaderboardByCategory).map(([category, drivers]: [string, any[]]) => (
                <div key={category} className="mb-10">
                  <div className="mb-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-2 py-3 bg-zinc-900 rounded-t-lg border-b border-zinc-800">
                    <div className="flex flex-wrap items-center gap-4">
                      <h2 className="text-xl font-bold text-white">{category}</h2>
                      <span className="text-sm text-gray-400">Drivers: <span className="font-semibold text-white">{categoryStats[category]?.driverCount ?? '-'}</span></span>
                      <span className="text-sm text-gray-400">Total Collected: <span className="font-semibold text-white">${categoryTotalAmount[category] ? categoryTotalAmount[category].toLocaleString() : '-'}</span></span>
                      <span className="text-sm text-gray-400">Re-Entries: <span className="font-semibold text-white">{categoryStats[category]?.reEntryCount ?? '-'}</span></span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 ml-auto">
                      {categoryPrizeInfo[category] && (
                        categoryPrizeInfo[category].usePercentage ? (
                          <>
                            <span className="text-sm text-green-400">1st Prize: <span className="font-semibold text-white">${((categoryTotalAmount[category] * (categoryPrizeInfo[category].firstPct || 0) / 100) || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</span></span>
                            <span className="text-sm text-blue-400">2nd Prize: <span className="font-semibold text-white">${((categoryTotalAmount[category] * (categoryPrizeInfo[category].secondPct || 0) / 100) || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</span></span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm text-green-400">1st Prize: <span className="font-semibold text-white">${(categoryPrizeInfo[category].firstFixed || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</span></span>
                            <span className="text-sm text-blue-400">2nd Prize: <span className="font-semibold text-white">${(categoryPrizeInfo[category].secondFixed || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</span></span>
                          </>
                        )
                      )}
                    </div>
                  </div>
                  <div className="bg-zinc-800 rounded-b-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-zinc-700">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Best Finish
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Race Number
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Driver
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Points
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Events
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-700">
                          {drivers.map((driver) => (
                            <tr key={driver.driver_id} className="hover:bg-zinc-750">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`text-sm font-medium ${getPositionColor(driver.position)}`}>
                                  {getPositionDisplay(driver.position)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-300">
                                  {driver.race_number || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-white">
                                  {driver.driver_name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-primary-500">
                                  {driver.total_points.toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-300">
                                  {driver.total_events}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* Series Points Tab */}
        {activeTab === 'series-points' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Series Points</h2>
                <p className="text-gray-400">Accumulated points across the dates of a series within one season</p>
              </div>
              {availableSeasons.length > 0 && (
                <div className="flex items-center gap-2">
                  <label htmlFor="season-select" className="text-gray-300 text-sm">Season:</label>
                  <select
                    id="season-select"
                    className="bg-zinc-900 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:ring-2 focus:ring-primary-500"
                    value={selectedSeason ?? ''}
                    onChange={e => setSelectedSeason(Number.parseInt(e.target.value))}
                  >
                    {availableSeasons.map(season => (
                      <option key={season} value={season}>{season}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {seriesLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="text-gray-400 mt-4">Loading series points...</p>
              </div>
            ) : Object.keys(seriesPointsByCategory).length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 rounded-lg">
                <BarChart3 className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">No series events found</h3>
                <p className="text-gray-400">
                  {selectedSeason
                    ? `No series results recorded for the ${selectedSeason} season.`
                    : 'No multi-date series events with tournament results available.'}
                </p>
              </div>
            ) : (
              Object.entries(seriesPointsByCategory).map(([category, drivers]: [string, SeriesPointsEntry[]]) => (
                <div key={category} className="mb-10">
                  <div className="mb-2 px-2 py-3 bg-zinc-900 rounded-t-lg border-b border-zinc-800">
                    <h2 className="text-xl font-bold text-white">{category}</h2>
                    <p className="text-sm text-gray-400">{selectedSeason} season standings</p>
                  </div>
                  <div className="bg-zinc-800 rounded-b-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-zinc-700">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Series
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Best Finish
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Race Number
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Driver
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Total Points
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Events
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-700">
                          {drivers.map((driver) => (
                            <tr 
                              key={`${driver.driver_id || driver.temporary_user_id}-${driver.series_name}-${driver.season}`} 
                              className="hover:bg-zinc-750 cursor-pointer"
                              onClick={() => openDriverDetails(driver)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-white">{driver.series_name}</div>
                                <div className="text-xs text-gray-400">{driver.season} season</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`text-sm font-medium ${getPositionColor(driver.position)}`}>
                                  {getPositionDisplay(driver.position)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-300">
                                  {driver.race_number || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-white">
                                  {driver.driver_name}
                                  {!driver.driver_id && driver.temporary_user_id && (
                                    <span className="ml-2 text-xs text-yellow-400 bg-yellow-900 px-2 py-1 rounded">
                                      Temp
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-primary-500">
                                  {driver.total_points.toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-300">
                                  {driver.events_participated}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Driver Details Overlay */}
      {showDriverOverlay && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedDriver.driver_name}</h3>
                  <p className="text-gray-400">{selectedDriver.series_name} {selectedDriver.season} - {selectedDriver.category_name}</p>
                </div>
                <button
                  onClick={() => setShowDriverOverlay(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-zinc-700 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-500">{selectedDriver.total_points}</div>
                    <div className="text-sm text-gray-400">Total Points</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{selectedDriver.events_participated}</div>
                    <div className="text-sm text-gray-400">Events Raced</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-500">
                      {getPositionDisplay(selectedDriver.position)}
                    </div>
                    <div className="text-sm text-gray-400">Series Position</div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Race Day Breakdown</h4>
                {selectedDriver.events.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No race days found
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDriver.events.map(event => (
                      <div key={`${event.event_id}-${event.category_id}`} className="flex items-center justify-between p-3 bg-zinc-700 rounded-lg">
                        <div className="flex-1">
                          <div className="text-white font-medium">{event.event_name}</div>
                          <div className="text-sm text-gray-400">{formatDateForDisplay(event.event_date)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-primary-500">{event.total_points} pts</div>
                          <div className="text-sm text-gray-400">
                            {getPositionDisplay(event.position)} place
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-zinc-700">
                <div className="text-center text-sm text-gray-400">
                  Click outside or press ESC to close
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}